export interface PlaceDetails {
  providerAddress: string;
  physicalCity: string;
  physicalState: string;
  physicalStateCode: string;
  physicalZip: string;
  country: string;
  countryCode: string;
  county: string;
  areaName: string;
  providerName: string;
  fullAddress: string;
  latitude: number | null;
  longitude: number | null;
}

export interface PlacePrediction {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

interface PlaceAddressInput {
  name?: string;
  address?: string;
  fullAddress?: string;
}

const getComponent = (
  components: any[],
  type: string,
  nameType: "longText" | "shortText" = "longText",
): string => {
  const comp = components.find((c) => c.types?.includes(type));
  return comp ? comp[nameType] || "" : "";
};

// Returns all autocomplete predictions for a query — US only
const fetchPredictions = async (
  apiKey: string,
  query: string,
): Promise<PlacePrediction[]> => {
  if (!query.trim()) return [];

  try {
    const res = await fetch(
      "https://places.googleapis.com/v1/places:autocomplete",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask":
            "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
        },
        body: JSON.stringify({ input: query, includedRegionCodes: ["us"] }),
      },
    );

    if (!res.ok) return [];

    const data = await res.json();
    const raw: any[] = data.suggestions || [];

    return raw
      .map((s) => ({
        placeId: s.placePrediction?.placeId || "",
        mainText:
          s.placePrediction?.structuredFormat?.mainText?.text ||
          s.placePrediction?.text?.text ||
          "",
        secondaryText:
          s.placePrediction?.structuredFormat?.secondaryText?.text || "",
      }))
      .filter((p) => p.placeId);
  } catch {
    return [];
  }
};

// Fetches full place details for placeId
const fetchPlaceDetails = async (
  apiKey: string,
  placeId: string,
  fallbackName: string,
): Promise<PlaceDetails | null> => {
  try {
    const res = await fetch(
      `https://places.googleapis.com/v1/places/${placeId}`,
      {
        headers: {
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask":
            "addressComponents,location,formattedAddress,displayName",
        },
      },
    );

    if (!res.ok) return null;

    const details = await res.json();
    const components: any[] = details.addressComponents || [];

    const streetNumber = getComponent(components, "street_number");
    const route = getComponent(components, "route");
    const streetAddress = streetNumber
      ? `${streetNumber} ${route}`.trim()
      : route;

    return {
      providerAddress:
        streetAddress || details.formattedAddress?.split(",")[0] || "",
      physicalCity:
        getComponent(components, "locality") ||
        getComponent(components, "administrative_area_level_3") ||
        getComponent(components, "administrative_area_level_2"),
      physicalState: getComponent(components, "administrative_area_level_1"),
      physicalStateCode: getComponent(
        components,
        "administrative_area_level_1",
        "shortText",
      ),
      physicalZip: getComponent(components, "postal_code"),
      country: getComponent(components, "country"),
      countryCode: getComponent(components, "country", "shortText"),
      county: getComponent(components, "administrative_area_level_2"),
      areaName: (() => {
        const neighborhood = getComponent(components, "neighborhood");
        if (neighborhood) return neighborhood;
        return (
          getComponent(components, "locality") ||
          getComponent(components, "sublocality_level_1") ||
          getComponent(components, "sublocality") ||
          ""
        );
      })(),
      providerName: details.displayName?.text || streetAddress || fallbackName,
      fullAddress: details.formattedAddress || fallbackName,
      latitude: details.location?.latitude ?? null,
      longitude: details.location?.longitude ?? null,
    };
  } catch {
    return null;
  }
};

/**
 * Returns autocomplete predictions for a search query.
 * Call this on input change to populate the Google fallback dropdown rows.
 * Call getPlaceDetailById when the user selects a prediction.
 */
export async function getAllPlacesByAddress(
  input: PlaceAddressInput,
): Promise<PlacePrediction[]> {
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_AUTOCOMPLETE_API_KEY;
  if (!apiKey) {
    console.warn("Google Places API key is not provided");
    return [];
  }

  const queries = [input.fullAddress, input.address, input.name].filter(
    (q): q is string => !!q?.trim(),
  );

  for (const query of queries) {
    const predictions = await fetchPredictions(apiKey, query);
    if (predictions.length) return predictions;
  }

  return [];
}

/**
 * Fetches full PlaceDetails for a selected prediction.
 * Call this in handleGoogleSelect when the user picks a row from the dropdown.
 */
export async function getPlaceDetailById(
  placeId: string,
  fallbackName = "",
): Promise<PlaceDetails | null> {
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_AUTOCOMPLETE_API_KEY;
  if (!apiKey) {
    console.warn("Google Places API key is not provided");
    return null;
  }

  return fetchPlaceDetails(apiKey, placeId, fallbackName);
}

/** Returns the first prediction's full details. Used for lat/lon lookups on manual location save. */
export async function getPlaceByAddress(
  input: PlaceAddressInput,
): Promise<PlaceDetails | null> {
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_AUTOCOMPLETE_API_KEY;
  if (!apiKey) {
    console.warn("Google Places API key is not provided");
    return null;
  }

  const queries = [input.fullAddress, input.address, input.name].filter(
    (q): q is string => !!q?.trim(),
  );

  const usedQuery = input.fullAddress || input.address || input.name || "";

  for (const query of queries) {
    const preds = await fetchPredictions(apiKey, query);
    if (preds.length) {
      return fetchPlaceDetails(apiKey, preds[0].placeId, usedQuery);
    }
  }

  return null;
}
